import { useState } from 'react'
import { Button } from '../../components/button'
import Icon from '../../components/icon'
import { AddDeliveryRegionModal } from '../../components/modal/app_modals/addDeliveryRegionModal'
import CustSwitch from '../../components/switch'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { useModalControl } from '../../hooks/useModalControl'
import {
  DeliveryRegion,
  DeliveryRegionImportCandidate,
} from '../../redux-toolkits/locations/locations.type'
import {
  useAddDeliveryRegionMutation,
  useFetchDeliveryRegionsQuery,
  useImportDeliveryRegionsMutation,
  useLazyPreviewDeliveryRegionImportQuery,
  useToggleDeliveryRegionMutation,
  useUpdateDeliveryRegionMutation,
} from '../../redux-toolkits/user/user.slice'

type CoordinateSummary = {
  longitude: string
  latitude: string
  pointCount: number
}

type RegionWithBoundary = Pick<
  DeliveryRegionImportCandidate,
  'boundary' | 'displayName' | 'name'
>

function getRegionId(value?: string | DeliveryRegion) {
  if (!value) return ''
  return typeof value === 'string' ? value : value._id
}

function flattenBoundaryPoints(candidate: RegionWithBoundary) {
  const { boundary } = candidate

  if (boundary.type === 'Polygon') {
    return (boundary.coordinates as number[][][]).flat(1)
  }

  return (boundary.coordinates as number[][][][]).flat(2)
}

function getCoordinateSummary(
  candidate: RegionWithBoundary,
): CoordinateSummary {
  const points = flattenBoundaryPoints(candidate).filter(
    (point) =>
      Array.isArray(point) &&
      point.length === 2 &&
      typeof point[0] === 'number' &&
      typeof point[1] === 'number',
  )

  const uniquePoints = points.filter(
    (point, index) =>
      index === 0 ||
      point[0] !== points[index - 1][0] ||
      point[1] !== points[index - 1][1],
  )

  if (uniquePoints.length < 1) {
    return {
      longitude: '-',
      latitude: '-',
      pointCount: 0,
    }
  }

  const totals = uniquePoints.reduce(
    (acc, [longitude, latitude]) => ({
      longitude: acc.longitude + longitude,
      latitude: acc.latitude + latitude,
    }),
    { longitude: 0, latitude: 0 },
  )

  return {
    longitude: (totals.longitude / uniquePoints.length).toFixed(6),
    latitude: (totals.latitude / uniquePoints.length).toFixed(6),
    pointCount: uniquePoints.length,
  }
}

const RegionCoordinateCard = ({
  candidate,
  checked,
  onToggle,
}: {
  candidate: DeliveryRegionImportCandidate
  checked?: boolean
  onToggle?: () => void
}) => {
  const coordinate = getCoordinateSummary(candidate)
  const content = (
    <>
      {onToggle && (
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onToggle}
          className="mt-1"
        />
      )}

      <span className="flex min-w-0 flex-1 flex-col gap-3">
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-sm font-medium text-black">
            {candidate.name}
          </span>
          {candidate.displayName && (
            <span className="break-words text-xs leading-5 text-sec-black">
              {candidate.displayName}
            </span>
          )}
        </span>

        <span className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <span className="rounded-[8px] bg-light-gray px-3 py-2">
            <span className="block text-[11px] text-sec-black">Longitude</span>
            <span className="block text-xs font-medium text-black">
              {coordinate.longitude}
            </span>
          </span>
          <span className="rounded-[8px] bg-light-gray px-3 py-2">
            <span className="block text-[11px] text-sec-black">Latitude</span>
            <span className="block text-xs font-medium text-black">
              {coordinate.latitude}
            </span>
          </span>
          <span className="rounded-[8px] bg-light-gray px-3 py-2">
            <span className="block text-[11px] text-sec-black">Boundary</span>
            <span className="block text-xs font-medium text-black">
              {candidate.boundary.type}
            </span>
          </span>
          <span className="rounded-[8px] bg-light-gray px-3 py-2">
            <span className="block text-[11px] text-sec-black">Points</span>
            <span className="block text-xs font-medium text-black">
              {coordinate.pointCount}
            </span>
          </span>
        </span>
      </span>
    </>
  )

  if (onToggle) {
    return (
      <label className="flex cursor-pointer gap-3 rounded-[8px] border border-divider-gray p-3">
        {content}
      </label>
    )
  }

  return (
    <div className="flex gap-3 rounded-[8px] border border-divider-gray p-3">
      {content}
    </div>
  )
}

const RegionActions = ({
  region,
  editingDeliveryRegion,
  onEdit,
  onToggle,
}: {
  region: DeliveryRegion
  editingDeliveryRegion?: boolean
  onEdit: () => void
  onToggle: () => void
}) => (
  <div className="flex items-center justify-end gap-3">
    <Button
      label="Edit"
      size="small"
      variant="border-grey"
      onClick={onEdit}
      type="button"
    />

    <button
      disabled={editingDeliveryRegion}
      className="flex w-fit items-center justify-end"
      onClick={onToggle}
      type="button"
    >
      <CustSwitch
        checked={region.isAvailable}
        disabled={editingDeliveryRegion}
      />
    </button>
  </div>
)

const RegionSummary = ({ region }: { region: DeliveryRegion }) => {
  const coordinate = getCoordinateSummary(region)

  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      <span className="rounded-[8px] bg-light-gray px-3 py-2">
        <span className="block text-[11px] text-sec-black">Longitude</span>
        <span className="block text-xs font-medium text-black">
          {coordinate.longitude}
        </span>
      </span>
      <span className="rounded-[8px] bg-light-gray px-3 py-2">
        <span className="block text-[11px] text-sec-black">Latitude</span>
        <span className="block text-xs font-medium text-black">
          {coordinate.latitude}
        </span>
      </span>
      <span className="rounded-[8px] bg-light-gray px-3 py-2">
        <span className="block text-[11px] text-sec-black">Boundary</span>
        <span className="block text-xs font-medium text-black">
          {region.boundary.type}
        </span>
      </span>
      <span className="rounded-[8px] bg-light-gray px-3 py-2">
        <span className="block text-[11px] text-sec-black">Points</span>
        <span className="block text-xs font-medium text-black">
          {coordinate.pointCount}
        </span>
      </span>
    </div>
  )
}

export const DeliveryRegions = () => {
  const [showRegions, setShowRegions] = useState(true)
  const [expandedParentIds, setExpandedParentIds] = useState<string[]>([])
  const [selectedRegion, setSelectedRegion] = useState<DeliveryRegion>()
  const [importQuery, setImportQuery] = useState('')
  const [childLimit, setChildLimit] = useState(30)
  const [selectedChildSlugs, setSelectedChildSlugs] = useState<string[]>([])

  const {
    modalOpen,
    modal,
    handleModalClose: closeModal,
    handleModalOpen,
    setModalOpen,
  } = useModalControl()

  const { data: deliveryRegionsData, isLoading: fetchingDeliveryRegions } =
    useFetchDeliveryRegionsQuery({
      limit: 1000,
      page: 1,
    })

  const [toggleDeliveryRegion, { isLoading: editingDeliveryRegion }] =
    useToggleDeliveryRegionMutation()

  const [addDeliveryRegion, { isLoading: addingDeliveryRegion }] =
    useAddDeliveryRegionMutation()

  const [updateDeliveryRegion, { isLoading: updatingDeliveryRegion }] =
    useUpdateDeliveryRegionMutation()
  const [
    previewDeliveryRegionImport,
    { data: importPreviewData, isFetching: previewingImport },
  ] = useLazyPreviewDeliveryRegionImportQuery()
  const [importDeliveryRegions, { isLoading: importingRegions }] =
    useImportDeliveryRegionsMutation()

  const importPreview = importPreviewData?.data
  const deliveryRegions = deliveryRegionsData?.data.docs ?? []
  const parentRegions = deliveryRegions.filter(
    (region) => !getRegionId(region.parentRegion),
  )
  const childrenByParentId = deliveryRegions.reduce<
    Record<string, DeliveryRegion[]>
  >((acc, region) => {
    const parentId = getRegionId(region.parentRegion)
    if (!parentId) {
      return acc
    }

    acc[parentId] = [...(acc[parentId] ?? []), region]
    return acc
  }, {})

  const toggleExpandedParent = (parentId: string) => {
    setExpandedParentIds((current) =>
      current.includes(parentId)
        ? current.filter((id) => id !== parentId)
        : [...current, parentId],
    )
  }

  const handleEditRegion = (region: DeliveryRegion) => {
    setSelectedRegion(region)
    handleModalOpen('editDeliveryRegionModal')
  }

  const handleToggleRegion = (region: DeliveryRegion) => {
    void toggleDeliveryRegion({
      id: region._id,
      isAvailable: !region.isAvailable,
    })
  }

  const handleModalClose = () => {
    setSelectedRegion(undefined)
    closeModal()
  }

  const handlePreviewImport = async () => {
    const query = importQuery.trim()
    if (!query) return

    try {
      const response = await previewDeliveryRegionImport({
        query,
        countryCode: 'ng',
        childLimit,
      }).unwrap()

      setSelectedChildSlugs(response.data.children.map((child) => child.slug))
    } catch {
      setSelectedChildSlugs([])
    }
  }

  const toggleSelectedChild = (child: DeliveryRegionImportCandidate) => {
    setSelectedChildSlugs((current) =>
      current.includes(child.slug)
        ? current.filter((slug) => slug !== child.slug)
        : [...current, child.slug],
    )
  }

  const handleImportRegions = async () => {
    if (!importPreview) return

    const selectedChildren = importPreview.children.filter((child) =>
      selectedChildSlugs.includes(child.slug),
    )

    const response = await importDeliveryRegions({
      parent: importPreview.parent,
      children: selectedChildren,
    })

    if (response.data?.success) {
      setImportQuery('')
      setSelectedChildSlugs([])
    }
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <HeaderSubheader
          title="Delivery regions"
          text="Configure macro delivery regions here."
          className="max-w-fit"
        />

        <Button
          label={'New'}
          primary
          onClick={() => {
            setSelectedRegion(undefined)
            handleModalOpen('addNewDeliveryRegionModal')
          }}
          size="medium"
          className="min-w-fit"
          type="button"
        />
      </div>

      <div className="flex flex-col gap-4 rounded-[8px] border border-divider-gray p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px_auto]">
          <input
            className="h-[48px] rounded-[8px] border border-divider-gray px-4 text-base outline-none"
            placeholder="Search region e.g Lagos Island"
            value={importQuery}
            onChange={(event) => {
              setImportQuery(event.target.value)
            }}
          />
          <input
            className="h-[48px] rounded-[8px] border border-divider-gray px-4 text-base outline-none"
            type="number"
            min={0}
            max={100}
            value={childLimit}
            onChange={(event) => {
              setChildLimit(Number(event.target.value) || 0)
            }}
          />
          <Button
            label="Generate"
            primary
            type="button"
            loading={previewingImport}
            disabled={!importQuery.trim() || previewingImport}
            onClick={handlePreviewImport}
          />
        </div>

        {importPreview && (
          <div className="flex flex-col gap-4 border-t border-divider-gray pt-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-black">
                Parent region
              </span>
              <RegionCoordinateCard candidate={importPreview.parent} />
            </div>

            {importPreview.children.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-black">
                  Sub-regions
                </span>
                <div className="grid grid-cols-1 gap-3">
                  {importPreview.children.map((child) => (
                    <RegionCoordinateCard
                      key={child.slug}
                      candidate={child}
                      checked={selectedChildSlugs.includes(child.slug)}
                      onToggle={() => toggleSelectedChild(child)}
                    />
                  ))}
                </div>
              </div>
            )}

            {importPreview.skippedChildren.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {importPreview.skippedChildren.map((child) => (
                  <span
                    key={`${child.name}-${child.reason}`}
                    className="rounded-[8px] bg-light-gray px-3 py-2 text-xs text-sec-black"
                  >
                    {child.name}: {child.reason}
                  </span>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                label="Import selected"
                primary
                type="button"
                loading={importingRegions}
                disabled={importingRegions}
                onClick={handleImportRegions}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-center justify-between">
        <span className="text-base text-black">
          Delivery in{' '}
          <span className="text-black font-medium">
            {deliveryRegionsData?.data.total ?? 0}
          </span>{' '}
          regions
        </span>

        <button
          type="button"
          className="text-black flex text-base gap-2 items-center w-fit"
          onClick={() => {
            setShowRegions(!showRegions)
          }}
        >
          <span className="font-medium">
            {showRegions ? 'Hide regions' : 'View regions'}
          </span>

          <Icon
            id="left-caret"
            width={14}
            height={14}
            className={`${showRegions ? 'rotate-90' : '-rotate-90'}
                transition-all duration-300
                `}
          />
        </button>
      </div>

      {showRegions && (
        <div className="flex w-full flex-col gap-3">
          {fetchingDeliveryRegions && (
            <div className="rounded-[8px] border border-divider-gray p-4 text-sm text-sec-black">
              Loading delivery regions...
            </div>
          )}

          {!fetchingDeliveryRegions && parentRegions.length < 1 && (
            <div className="rounded-[8px] border border-divider-gray p-4 text-sm text-sec-black">
              No delivery region has been configured
            </div>
          )}

          {!fetchingDeliveryRegions &&
            parentRegions.map((parentRegion) => {
              const children = childrenByParentId[parentRegion._id] ?? []
              const expanded = expandedParentIds.includes(parentRegion._id)

              return (
                <div
                  key={parentRegion._id}
                  className="rounded-[8px] border border-divider-gray"
                >
                  <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_360px_auto] lg:items-center">
                    <button
                      type="button"
                      className="flex min-w-0 items-start gap-3 text-left"
                      onClick={() => {
                        toggleExpandedParent(parentRegion._id)
                      }}
                    >
                      <Icon
                        id="left-caret"
                        width={14}
                        height={14}
                        className={`mt-1 min-w-fit transition-all duration-300 ${
                          expanded ? 'rotate-90' : '-rotate-90'
                        }`}
                      />

                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="truncate text-base font-medium text-black">
                          {parentRegion.name}
                        </span>
                        <span className="text-xs text-sec-black">
                          {parentRegion.slug}
                        </span>
                        <span className="text-xs text-sec-black">
                          {children.length}{' '}
                          {children.length === 1 ? 'sub-region' : 'sub-regions'}
                        </span>
                      </span>
                    </button>

                    <RegionSummary region={parentRegion} />

                    <RegionActions
                      region={parentRegion}
                      editingDeliveryRegion={editingDeliveryRegion}
                      onEdit={() => handleEditRegion(parentRegion)}
                      onToggle={() => handleToggleRegion(parentRegion)}
                    />
                  </div>

                  {expanded && (
                    <div className="flex flex-col gap-3 border-t border-divider-gray bg-light-gray/40 p-4">
                      {children.length < 1 && (
                        <p className="text-sm text-sec-black">
                          No sub-region has been added under {parentRegion.name}
                          .
                        </p>
                      )}

                      {children.map((childRegion) => (
                        <div
                          key={childRegion._id}
                          className="grid grid-cols-1 gap-4 rounded-[8px] border border-divider-gray bg-white p-3 lg:grid-cols-[1fr_360px_auto] lg:items-center"
                        >
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="truncate text-sm font-medium text-black">
                              {childRegion.name}
                            </span>
                            <span className="text-xs text-sec-black">
                              {childRegion.slug}
                            </span>
                          </div>

                          <RegionSummary region={childRegion} />

                          <RegionActions
                            region={childRegion}
                            editingDeliveryRegion={editingDeliveryRegion}
                            onEdit={() => handleEditRegion(childRegion)}
                            onToggle={() => handleToggleRegion(childRegion)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
        </div>
      )}

      <AddDeliveryRegionModal
        open={
          modalOpen &&
          (modal === 'addNewDeliveryRegionModal' ||
            modal === 'editDeliveryRegionModal')
        }
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async (regionData, resetForm) => {
          const resp = selectedRegion
            ? await updateDeliveryRegion({
                id: selectedRegion._id,
                payload: regionData,
              })
            : await addDeliveryRegion(regionData)

          if (resp.data?.success) {
            resetForm()
            handleModalClose()
          }
        }}
        loading={addingDeliveryRegion || updatingDeliveryRegion}
        region={selectedRegion}
      />
    </div>
  )
}
